import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import BigNumber from 'bignumber.js';

import createSafeContext from '#/utils/createSafeContext';
import { FundingPaymentNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import {
  getFundingPayments,
  FundingPayment,
  FundingPaymentsResp,
} from '#/api/funding-payments';
import { shortDateFormat, timeFormat } from '#/utils/date';
import { useWalletView } from '#/features/wallet/wallet-context';

const adapter = createEntityAdapter<FundingPayment>({
  sortComparer: (paymentA, paymentB) =>
    paymentB.created_at.getTime() - paymentA.created_at.getTime(),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: FundingPaymentsState = {
  status: 'connected',
  error: '',
  fundingPayments: adapter.getInitialState(),
};

export interface FundingPaymentsState extends LiveConnectionBaseState {
  readonly fundingPayments: EntityState<FundingPayment>;
}
export interface FundingPaymentsView
  extends Omit<FundingPaymentsState, 'fundingPayments'> {
  readonly fundingPayments: readonly FundingPaymentView[];
}

export const [useFundingPaymentsView, FundingPaymentsViewCtx] =
  createSafeContext<FundingPaymentsView>('FundingPaymentsView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function FundingPaymentsProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);

  const view = useMemo(() => createView(state), [state]);

  useFundingPaymentsLiveConnection(setState);

  return (
    <FundingPaymentsViewCtx value={view}>
      {props.children}
    </FundingPaymentsViewCtx>
  );
}

export interface FundingPaymentView {
  readonly id: string;
  readonly market: string;
  readonly createdTime: string;
  readonly createdDate: string;
  readonly payment: string;
  readonly paymentChangeDirection: 'up' | 'down';
}

export function createView(state: FundingPaymentsState): FundingPaymentsView {
  const fundingPayments = selectors
    .selectAll(state.fundingPayments)
    .map((fundingPayment) => fundingPaymentView(fundingPayment));

  return {
    ...state,
    fundingPayments,
  };
}

const changeDirectionView = (value: BigNumber) =>
  value.isPositive() ? 'up' : 'down';

function fundingPaymentView(
  fundingPayment: FundingPayment,
): FundingPaymentView {
  const createdTime = timeFormat(fundingPayment.created_at);

  const createdDate = shortDateFormat(fundingPayment.created_at);

  const payment = `${
    fundingPayment.payment.isPositive() ? '+' : '-'
  }$${fundingPayment.payment.abs().toFormat(4)}`;

  const paymentChangeDirection = changeDirectionView(fundingPayment.payment);

  return {
    id: fundingPayment.id,
    market: fundingPayment.market,
    createdTime,
    createdDate,
    payment,
    paymentChangeDirection,
  };
}

function useFundingPaymentsLiveConnection(
  setState: Dispatch<SetStateAction<FundingPaymentsState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;
  const channel = 'funding_payments.ALL';

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getFundingPayments({
        signal,
      }),
    [],
  );

  const setAll = useCallback(
    (data: FundingPaymentsResp) => {
      setState((state) => ({
        ...state,
        fundingPayments: adapter.setAll(state.fundingPayments, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      fundingPayments: adapter.removeAll(state.fundingPayments),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: FundingPaymentNotification) => {
      setState((state) => ({
        ...state,
        fundingPayments: adapter.setOne(state.fundingPayments, notif.data),
      }));
    },
    [setState],
  );

  useLiveConnection<
    FundingPaymentsResp,
    FundingPaymentsState,
    FundingPaymentNotification
  >({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
